import { useCallback, useState } from 'react';
import type { DocNode } from '@atlaskit/adf-schema';
import { performIssuePostRequest } from '@atlassian/jira-issue-reliability-fetch/src/index.tsx';
import { useIssueKey, useIssueId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { useProjectId, useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import {
	DescriptionKey,
	SummaryKey,
} from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { convertAdfToText } from '@atlassian/jira-rich-content/src/common/convert-adf-to-text/index.tsx';
import type { RelatedIssue } from '../../controllers/context.tsx';

type Data = {
	data: RelatedIssue[] | undefined;
	error: unknown | undefined;
	loading: boolean;
};

// Search is available only for issue creation from GIC
export type RelatedIssuesSearchPayload = {
	issue_summary: string;
	project_id: string;
	issue_description?: DocNode;
	minimum_sim_score?: number;
	/** Flag to indicate if this is AGG data */
	isAggData?: boolean;
	_error?: unknown;
	_loading?: boolean;
	_aggData?: RelatedIssue[];
	/** Flag to skip AI interaction analytics */
	_skipAiInteractionAnalytics?: boolean;
	/** Flag to skip AI result analytics */
	_skipAiResultAnalytics?: boolean;
};

type RelatedIssuesResponse = {
	issue_id: string;
	issue_key: string;
	issue_link: string;
	issue_type_icon_url: string;
	issue_summary: string;
};

type RelatedIssuesAiFeatureInput = {
	issue_key: string;
	issue_id?: string;
	issue_summary: string;
	issue_description: string | null;
	project_id: string | undefined;
	existing_issues: string[];
	minimum_sim_score?: number;
};

const RELATED_ISSUES_API = '/gateway/api/assist/ai/v2/ai-feature/jirasimilarissues';

const DEFAULT_MIN_SIM_SCORE = 0.5;

export const parseData = (input: {
	ai_feature_output: { issues: RelatedIssuesResponse[] };
}): RelatedIssue[] => {
	return input?.ai_feature_output?.issues?.map((issue) => ({
		id: issue.issue_id,
		issueKey: issue.issue_key,
		issueLink: issue.issue_link,
		issueTypeIconUrl: issue.issue_type_icon_url,
		issueSummary: issue.issue_summary,
	}));
};

export const useFetchRelatedIssue = (existingIssues: string[]) => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectId = useProjectId(projectKey);
	const issueId = useIssueId();

	const [descriptionValue] = useFieldValue({
		issueKey,
		fieldKey: DescriptionKey,
	});

	const [summaryValue] = useFieldValue({
		issueKey,
		fieldKey: SummaryKey,
	});
	const [state, setState] = useState<Data>({
		data: undefined,
		error: undefined,
		loading: false,
	});

	const fetchSuggestion = useCallback(
		async (searchPayload?: RelatedIssuesSearchPayload) => {
			// Check if AGG call
			if (searchPayload?.isAggData) {
				if (searchPayload._error) {
					setState({
						error: searchPayload._error,
						data: undefined,
						loading: false,
					});
					return;
				}

				setState({
					error: undefined,
					data: searchPayload._aggData,
					loading: searchPayload._loading || false,
				});
				return;
			}

			const payload: { ai_feature_input: RelatedIssuesAiFeatureInput } = {
				ai_feature_input: {
					issue_key: issueKey,
					issue_id: issueId,
					issue_summary: summaryValue,
					issue_description: convertAdfToText(descriptionValue),
					project_id: projectId,
					existing_issues: existingIssues,
				},
			};
			if (searchPayload) {
				payload.ai_feature_input = {
					...payload.ai_feature_input,
					issue_summary: searchPayload.issue_summary,
					issue_description: convertAdfToText(searchPayload.issue_description),
					project_id: searchPayload.project_id,
					// issue_id and issue_key won't be available at the time of issue creation.
					// the similar issue api always expects issue_key so setting it to empty string
					issue_id: '',
					issue_key: '',
					minimum_sim_score: DEFAULT_MIN_SIM_SCORE,
				};
			}
			try {
				setState({
					error: undefined,
					data: undefined,
					loading: true,
				});
				const result = await performIssuePostRequest(
					RELATED_ISSUES_API,
					{
						headers: {
							'Content-Type': 'application/json',
							'X-Product': 'jira',
							'X-Experience-Id': 'jira-similar-issues',
						},
						body: JSON.stringify(payload),
					},
					'ai-improve-issue/ai-related-issues/src/services/use-fetch-related-issues',
				);
				setState({
					error: undefined,
					data: parseData(result),
					loading: false,
				});
				return result;
			} catch (error: unknown) {
				setState({
					error,
					data: undefined,
					loading: false,
				});
				return undefined;
			}
		},
		[descriptionValue, existingIssues, issueKey, issueId, projectId, summaryValue],
	);

	return {
		data: state.data,
		error: state.error,
		loading: state.loading,
		fetchSuggestion,
	};
};
